/**
 * Estado de la apariencia: lee las preferencias, las aplica a la página y las guarda.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { aplicarTema, guardarCacheTema } from './aplicar';
import {
  PREFERENCIAS_INICIALES,
  guardarPreferenciasTema,
  leerPreferenciasTema,
  sanearPreferencias,
  type PreferenciasTema,
} from './preferencias';

interface ValorTema {
  preferencias: PreferenciasTema;
  /** Cambia solo lo indicado; lo demás se mantiene. */
  cambiar: (cambios: Partial<PreferenciasTema>) => void;
  restablecer: () => void;
}

const ContextoTema = createContext<ValorTema | null>(null);

/** Aplica lo guardado antes de dibujar (se llama desde `main.tsx`) para evitar el parpadeo. */
export function aplicarTemaGuardado(): void {
  const guardadas = leerPreferenciasTema();
  aplicarTema(guardadas);
  guardarCacheTema(guardadas);
}

export function ProveedorTema({ children }: { children: ReactNode }) {
  const [preferencias, setPreferencias] = useState<PreferenciasTema>(leerPreferenciasTema);

  const poner = useCallback((nuevas: PreferenciasTema) => {
    setPreferencias(nuevas);
    aplicarTema(nuevas);
    guardarPreferenciasTema(nuevas);
    guardarCacheTema(nuevas);
  }, []);

  const valor = useMemo<ValorTema>(
    () => ({
      preferencias,
      cambiar: (cambios) => poner(sanearPreferencias({ ...preferencias, ...cambios })),
      restablecer: () => poner(PREFERENCIAS_INICIALES),
    }),
    [preferencias, poner],
  );

  return <ContextoTema.Provider value={valor}>{children}</ContextoTema.Provider>;
}

export function useTema(): ValorTema {
  const valor = useContext(ContextoTema);
  if (!valor) throw new Error('useTema debe usarse dentro de <ProveedorTema>.');
  return valor;
}
